import type { FieldValues, UseFormSetError } from 'react-hook-form';
import useRequestEmailVerification from '~/client/components/Auth/Register/RegisterBody/FormRegister/useFormRegister/useHandleRegister/useRequestEmailVerification';
import setDataToGib from '~/client/components/Auth/Shared/utils/setDataToGib';
import useUpdateGtmUserData from '~/client/components/Auth/Shared/utils/useUpdateGtmUserData';
import startFraudEngine from '~/client/shared/fraud/fraudEngine';
import useCheckWalletAuthErrors from '~/client/shared/WalletAuthentication/hooks/useCheckWalletAuthErrors';
import useAccountPopoverStore from '~/client/store/useAccountPopoverStore';
import type { UserManagementError } from '~/schema/public';
import trackEvent from '~/shared/trackEvent';
import Cookie from '~/shared/utils/Cookie';
import nativeReplace from '~/shared/utils/router/nativeReplace';

type MetamaskResponseArgs<T extends FieldValues> = {
  data: ResponseData;
  setError?: UseFormSetError<T>;
  setErrors?: (errorsFragment: GenericErrorType[]) => void;
  setOtpError?: () => void;
  otp?: string;
  redirectUrl: string;
  address: string;
};

type ResponseData = {
  token?: string;
  deviceDataToken?: string;
  errors?: UserManagementError[];
  sessionId?: string;
  userManagement?: {
    currentProfile?: {
      _id?: string;
    };
  };
};

const useHandleMetamaskAuthResponse = () => {
  const { handleAuthErrors } = useCheckWalletAuthErrors();
  const { updateGtmUserData } = useUpdateGtmUserData();
  const { requestEmailVerification } = useRequestEmailVerification();
  const { dismissPopover } = useAccountPopoverStore();
  const handleMetamaskAuthResponse = async <T extends FieldValues>({
    setError,
    setErrors,
    setOtpError,
    redirectUrl,
    data,
    address,
  }: MetamaskResponseArgs<T>) => {
    const { token, deviceDataToken, errors, sessionId, userManagement } = data;
    handleAuthErrors({
      errors,
      setError,
      setErrors,
      setOtpError,
      redirectUrl,
      address,
    });

    if (token) {
      Cookie.set('token', token);
      startFraudEngine({ deviceDataToken });
      await updateGtmUserData({ authToken: token });
      trackEvent({}, 'registrationSuccess');
      trackEvent({}, 'completeRegistrationSuccess');
      requestEmailVerification();
      dismissPopover(true);
      if (userManagement?.currentProfile?._id && sessionId) {
        setDataToGib(userManagement?.currentProfile?._id, sessionId);
      }
      return nativeReplace(redirectUrl);
    }

    return null;
  };
  return {
    handleMetamaskAuthResponse,
  };
};

export default useHandleMetamaskAuthResponse;
